import React from 'react';
import { 
  TrendingUp, 
  TrendingDown,
  Calendar,
  AlertTriangle,
  Target,
  Users,
  RefreshCw,
  Sparkles,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Plus,
  ExternalLink
} from 'lucide-react';

interface BusinessPatternProps {
  pattern: {
    id: string;
    pattern_type: 'correlation' | 'seasonality' | 'customer_behavior' | 'anomaly' | 'trend' | 'risk' | 'opportunity';
    title: string;
    description: string;
    confidence_score: number;
    occurrences_count: number;
    estimated_impact_eur?: number;
    impact_category?: string;
    next_occurrence_predicted_at?: string;
    prediction_confidence?: number;
    first_detected_at: string;
    is_validated_by_user: boolean;
    user_feedback?: string;
    notion_action_id?: string;
    notion_action_created_at?: string;
  };
  onValidate?: (patternId: string, isValidated: boolean, feedback?: string) => void;
  onDelete?: (patternId: string) => void;
  onCreateNotionAction?: (patternId: string) => void;
}

const BusinessPattern: React.FC<BusinessPatternProps> = ({ pattern, onValidate, onDelete, onCreateNotionAction }) => {
  
  // Couleurs et icônes par type
  const typeConfig = {
    correlation: {
      color: 'from-purple-600 to-cyan-500',
      bgLight: 'bg-blue-50/80',
      border: 'border-blue-200/60',
      text: 'text-blue-700',
      icon: <RefreshCw className="w-4 h-4" />,
      label: 'Corrélation'
    },
    seasonality: {
      color: 'from-green-500 to-emerald-500',
      bgLight: 'bg-green-50/80',
      border: 'border-green-200/60',
      text: 'text-green-700',
      icon: <Calendar className="w-4 h-4" />,
      label: 'Saisonnalité'
    },
    customer_behavior: {
      color: 'from-purple-500 to-fuchsia-500',
      bgLight: 'bg-purple-50/80',
      border: 'border-purple-200/60',
      text: 'text-purple-700',
      icon: <Users className="w-4 h-4" />,
      label: 'Comportement'
    },
    anomaly: {
      color: 'from-red-500 to-rose-500',
      bgLight: 'bg-red-50/80',
      border: 'border-red-200/60',
      text: 'text-red-700',
      icon: <AlertTriangle className="w-4 h-4" />,
      label: 'Anomalie'
    },
    trend: {
      color: 'from-yellow-500 to-indigo-500',
      bgLight: 'bg-yellow-50/80',
      border: 'border-yellow-200/60',
      text: 'text-yellow-700',
      icon: <TrendingUp className="w-4 h-4" />,
      label: 'Tendance'
    },
    risk: {
      color: 'from-purple-600 dark:from-purple-500 to-red-500',
      bgLight: 'bg-purple-50/80',
      border: 'border-purple-200/60',
      text: 'text-purple-700',
      icon: <AlertTriangle className="w-4 h-4" />,
      label: 'Risque'
    },
    opportunity: {
      color: 'from-indigo-500 to-blue-500',
      bgLight: 'bg-indigo-50/80',
      border: 'border-indigo-200/60',
      text: 'text-indigo-700',
      icon: <Target className="w-4 h-4" />,
      label: 'Opportunité'
    }
  };

  const config = typeConfig[pattern.pattern_type] || typeConfig.trend;
  
  return (
    <div 
      className="group relative rounded-xl border p-5 transition-all duration-300 cursor-pointer border-theme hover:border-theme hover:shadow-md bg-gradient-to-br from-blue-50/80 to-purple-50/80 dark:from-blue-900/20 dark:to-purple-900/20"
    >
      
      {/* Header avec badge type */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          {/* Badge type avec gradient */}
          <div className={`px-3 py-1.5 rounded-lg bg-gradient-to-r ${config.color} text-white text-xs font-semibold flex items-center space-x-1.5 shadow-sm`}>
            {config.icon}
            <span>{config.label}</span>
          </div>
          
          {/* Métadonnées */}
          <div className="flex items-center space-x-3 text-xs text-theme-secondary">
            <span className="flex items-center space-x-1">
              <Sparkles className="w-3 h-3" />
              <span>{Math.round(pattern.confidence_score * 100)}% confiance</span>
            </span>
            <span className="opacity-40">•</span>
            <span>{pattern.occurrences_count}x détecté</span>
          </div>
        </div>
        
        {/* Statut validation */}
        {pattern.is_validated_by_user && (
          <div className="flex items-center space-x-1.5 text-green-600 text-xs font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Validé</span>
          </div>
        )}
      </div>
      
      {/* Titre */}
      <h4 className="font-bold text-theme-primary text-lg mb-2 leading-tight">
        {pattern.title}
      </h4>
      
      {/* Description */}
      <p className="text-sm text-theme-primary leading-relaxed mb-4">
        {pattern.description}
      </p>
      
      {/* Impact financier */}
      {pattern.estimated_impact_eur && (
        <div className="flex items-center space-x-2 mb-3">
          {pattern.estimated_impact_eur > 0 ? (
            <TrendingUp className="w-4 h-4 text-green-600" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-600" />
          )}
          <span className={`text-sm font-semibold ${pattern.estimated_impact_eur > 0 ? 'text-green-700' : 'text-red-700'}`}>
            Impact estimé: {pattern.estimated_impact_eur > 0 ? '+' : ''}{pattern.estimated_impact_eur.toLocaleString()}€
          </span>
        </div>
      )}
      
      {/* Prédiction */}
      {pattern.next_occurrence_predicted_at && (
        <div className="flex items-center space-x-2 px-3 py-2 bg-theme-hover rounded-lg border border-theme mb-4">
          <Calendar className="w-4 h-4 text-accent-tertiary" />
          <div className="flex-1">
            <span className="text-xs text-theme-secondary block">Prochaine occurrence prévue</span>
            <span className="text-sm font-semibold text-theme-primary">
              {new Date(pattern.next_occurrence_predicted_at).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
            {pattern.prediction_confidence && (
              <span className="text-xs text-theme-secondary ml-2">
                ({Math.round(pattern.prediction_confidence * 100)}% confiance)
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Footer avec actions */}
      <div className="flex items-center justify-between pt-3 border-t border-theme">
        <div className="text-xs text-theme-secondary">
          Détecté le {new Date(pattern.first_detected_at).toLocaleDateString('fr-FR')}
        </div>
        
        {/* Actions (visibles au hover) */}
        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {/* Bouton Notion */}
          {pattern.notion_action_id ? (
            <div className="flex items-center space-x-1 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-lg border border-purple-200/50">
              <CheckCircle2 className="w-3 h-3" />
              <span>Dans Notion</span>
              <ExternalLink className="w-3 h-3" />
            </div>
          ) : onCreateNotionAction && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCreateNotionAction(pattern.id);
              }}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-xs font-medium rounded-lg transition-all flex items-center space-x-1 shadow-sm hover:shadow-md"
              title="Créer action dans Notion"
            >
              <Plus className="w-3 h-3" />
              <span>Notion</span>
            </button>
          )}
          
          {!pattern.is_validated_by_user && onValidate && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onValidate(pattern.id, true);
                }}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 shadow-sm"
                title="Valider ce pattern"
              >
                <CheckCircle2 className="w-3 h-3" />
                <span>Valider</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onValidate(pattern.id, false, 'Pattern rejeté');
                }}
                className="px-3 py-1.5 bg-white/70 hover:bg-red-50 text-gray-600 hover:text-red-600 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 border border-gray-200/60"
                title="Rejeter ce pattern"
              >
                <XCircle className="w-3 h-3" />
                <span>Rejeter</span>
              </button>
            </>
          )}
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Ouvrir modal détails
            }}
            className="p-1.5 bg-white/70 hover:bg-purple-50 text-purple-600 rounded-lg border border-purple-200/60 transition-colors shadow-sm"
            title="Voir détails"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessPattern;

